import styled, { useTheme } from 'styled-components'
import { Link } from 'react-router-dom'

import { LeaderboardPosition } from '../hooks/useLeaderboardPositions'
import { Typography } from 'components/Typography/Typography'
import Pagination from 'components/Pagination/Pagination'
import { ProtocolLpPool } from 'modules/MyPositions/components/ProtocolLpPool'
import { getFormattedFeeRate } from './utils'
import { ellipsizeTokenAddress, getDisplayTimestamp, getPositionStatusLabel } from 'utils/helpers'
import { ClonePositionLink } from '../components/ClonePositionLink'
import { PositionRange } from 'modules/MyPositions/components/PositionRange'
import { getPctValue } from 'utils/pctFormatter'
import { Status } from 'modules/MyPositions/MyPositions'
import { getFormattedUsdValue } from 'utils/usdFormatters'
import { TableContentLoader } from '../components/TableContentLoader'

const Container = styled.div`
  margin-top: 24px;
`

const ListItem = styled.div`
  border: 1px solid ${p => p.theme.colors.divider};
  padding: 12px 8px;
  margin-bottom: 8px;
  border-radius: 16px;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 6px;
  border-bottom: 1px solid ${p => p.theme.colors.divider};
`

const Row = styled.div`
  display: flex;
  padding: 6px;
  align-items: center;
  gap: 12px;
  & > div {
    flex: 1;
  }
`

const RowItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  a {
    color: ${p => p.theme.colors.text.primary};
  }
`

interface Props {
  paginatedItems: LeaderboardPosition[]
  currentPage: number
  totalPages: number
  handlePageChange: (page: number) => void
  isLoading: boolean
}

export const PositionsList = ({ paginatedItems, currentPage, totalPages, handlePageChange, isLoading }: Props) => {
  const theme = useTheme()

  if (isLoading) {
    return (
      <Container>
        <TableContentLoader />
      </Container>
    )
  }

  return (
    <Container>
      {paginatedItems.map(d => (
        <ListItem key={d.positionId}>
          <Header>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="caption" fontWeight="semiBold" style={{ marginRight: 8 }}>
                #{d.rank}
              </Typography>
              <ProtocolLpPool displayInfo={d.displayInfo} />
              <Typography variant="caption" style={{ marginLeft: 4 }}>
                {getFormattedFeeRate(d.displayInfo.feeRate)}
              </Typography>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="caption" style={{ marginRight: 32 }}>
                {d.displayInfo.creationTimestampMs ? getDisplayTimestamp(d.displayInfo.creationTimestampMs) : ''}
              </Typography>
              <ClonePositionLink position={d} />
            </div>
          </Header>
          <Row>
            <Typography variant="caption">
              <PositionRange displayInfo={d.displayInfo} />
            </Typography>
            <RowItem>
              <Typography variant="caption" color="secondary">
                APR:
              </Typography>
              <Typography variant="caption">{typeof d.aprFull === 'number' ? getPctValue(d.aprFull) : '-'}</Typography>
            </RowItem>
          </Row>
          <Row>
            <RowItem>
              <Typography variant="caption" color="secondary">
                Status:
              </Typography>
              <Typography variant="caption">
                <Status $type={d.displayInfo.status}>{getPositionStatusLabel(d.displayInfo.status)}</Status>
              </Typography>
            </RowItem>
            <RowItem>
              <Typography variant="caption" color="secondary">
                PnL:
              </Typography>
              <Typography variant="caption">{getFormattedUsdValue(d.pnl)}</Typography>
            </RowItem>
          </Row>
          <Row>
            <RowItem>
              <Typography variant="caption" color="secondary">
                Wallet:
              </Typography>
              <Typography variant="caption">
                <Link to={`https://suivision.xyz/account/${d.ownerId}`} target="_blank" rel="noreferrer">
                  {ellipsizeTokenAddress(d.ownerId)}
                </Link>
              </Typography>
            </RowItem>
            <RowItem>
              <Typography variant="caption" color="secondary">
                ROI:
              </Typography>
              <Typography variant="caption">{getPctValue(d.returnOnEquity)}</Typography>
            </RowItem>
          </Row>
          <Row>
            <RowItem>
              <Typography variant="caption" color="secondary">
                Equity:
              </Typography>
              <Typography variant="caption">
                {getFormattedUsdValue(d.timeWeightedEquity)}&nbsp;
                <span style={{ color: theme.colors.text.secondary }}>{`(${
                  d.displayInfo.leverage && d.displayInfo.leverage.isFinite()
                    ? d.displayInfo.leverage.toFixed(2) + 'x'
                    : '-'
                })`}</span>
              </Typography>
            </RowItem>
            <RowItem>
              <Typography variant="caption" color="secondary">
                HODL:
              </Typography>
              <Typography variant="caption">{getFormattedUsdValue(d.hodlBenchmarkPnl)}</Typography>
            </RowItem>
          </Row>
        </ListItem>
      ))}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        type="leaderboard-wallets"
      />
    </Container>
  )
}
